// Public /landing page: the fbzipper.com marketing site (ported from fbzipper-new-landing),
// Hebrew only. Registration and login run against the app's own API — the same calls as
// /auth/register (affiliateRegister) and /auth/login — and then open the app, where a
// brand-new account is sent through /welcome-tutorial first (see AppRoutes).
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { T as t } from './strings.js';
import Demo from './Demo.jsx';
import { Logo, WHATSAPP, WA_PATH } from './brand.jsx';
import { TESTIMONIALS, thumb } from './testimonials.js';
import { useAuth } from '../../modules/auth';
import { affiliateRegister, login, trackReferralClick } from '../../modules/auth/core/_requests';
import { normaliseIlMobile } from '../../modules/auth/components/Yad2Register';
import { TurnstileWidget, TURNSTILE_ENABLED } from '../../modules/auth/components/TurnstileWidget';
import { TutorialDemo } from '../tutorial-demo/TutorialDemo';
import { ReelDemo } from '../tutorial-demo/ReelDemo';
import './landing.scss';
import './TutorialDialog.scss';

const waLink = (text) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(text)}`;

// Where a signed-in visitor goes. A new account is redirected on to /welcome-tutorial.
const APP_HOME = '/campaigns';

// Affiliate referral: ?aff= (the app's param) or ?ref= (the old landing's), remembered in
// localStorage under the same key the /auth/register page reads.
function useReferral() {
  const [aff, setAff] = useState('');
  useEffect(() => {
    let code = '';
    try {
      const p = new URLSearchParams(window.location.search);
      const fromUrl = (p.get('aff') || p.get('ref') || '').trim();
      code = fromUrl || localStorage.getItem('aff') || '';
      if (fromUrl) {
        localStorage.setItem('aff', fromUrl);
        const key = `aff_click_tracked_${fromUrl}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          trackReferralClick(fromUrl).catch(() => {});
        }
      }
    } catch {}
    setAff(code);
  }, []);
  return aff;
}

function Highlighted({ parts }) {
  const [pre, mark, post] = parts;
  return (
    <>
      {pre}
      <span className="hl">{mark}</span>
      {post}
    </>
  );
}

// Keeps a <dialog> in step with an `open` flag.
function useDialog(open) {
  const ref = useRef(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return ref;
}

// Register (7-day free trial) and login, in one dialog. `mode` is 'register' | 'login' | null.
function AuthDialog({ mode, setMode, aff }) {
  const ref = useDialog(Boolean(mode));
  const { saveAuth } = useAuth();
  const [state, setState] = useState('idle'); // idle | busy | done
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const c = mode === 'login' ? t.auth.login : t.auth.register;

  useEffect(() => {
    setError('');
    setState('idle');
  }, [mode]);

  // Signed in: store the token and open the app with a full load, so the app boots
  // (and runs its gates — tutorial, onboarding, payment) exactly as after a normal login.
  function enter(apiToken) {
    saveAuth({ api_token: apiToken });
    if (mode === 'register') {
      // Same flag the /auth/register page sets: arms the post-register onboarding wizard.
      try { localStorage.setItem('onboarding_pending', '1'); } catch {}
    }
    setState('done');
    setTimeout(() => window.location.assign(APP_HOME), 900);
  }

  async function submitRegister(e) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const r = t.auth.register;
    const name = String(f.get('name') || '').trim();
    const phone = normaliseIlMobile(String(f.get('phone') || ''));
    const password = String(f.get('password') || '');
    if (!name) return setError(r.missingName);
    if (!phone) return setError(r.badPhone);
    if (password.length < 4) return setError(r.badPassword);
    setError('');
    setState('busy');
    try {
      const { data } = await affiliateRegister({
        yourname: name,
        phone,
        email: String(f.get('email') || '').trim(),
        password,
        aff,
        agent: null,
        turnstileToken,
      });
      if (!data?.success || !data.api_token) throw new Error(data?.message || r.error);
      enter(data.api_token);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || r.error);
      setState('idle');
    }
  }

  async function submitLogin(e) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setError('');
    setState('busy');
    try {
      const { data } = await login(String(f.get('email') || '').trim(), String(f.get('password') || ''));
      if (!data?.api_token) throw new Error();
      enter(data.api_token);
    } catch {
      setError(t.auth.login.wrong);
      setState('idle');
    }
  }

  const locked = state !== 'idle';
  return (
    <dialog ref={ref} className="checkout" onClose={() => setMode(null)} aria-labelledby="auth-h">
      {mode && (
        <form onSubmit={mode === 'login' ? submitLogin : submitRegister} noValidate>
          <div className="co-head">
            <h2 id="auth-h">{mode === 'register' ? `🎁 ${c.title}` : c.title}</h2>
            <button type="button" className="x" onClick={() => setMode(null)} aria-label={t.auth.close}>×</button>
          </div>
          <p className="co-sub">{c.sub}</p>
          {state === 'done' ? (
            <p className="form-ok" role="status">{c.done}</p>
          ) : mode === 'register' ? (
            <>
              <label><span>{c.name}</span><input name="name" required maxLength={80} autoComplete="name" /></label>
              <label><span>{c.phone}</span><input name="phone" type="tel" required maxLength={30} autoComplete="tel" dir="ltr" placeholder="050-1234567" /></label>
              <label><span>{c.email}</span><input name="email" type="email" maxLength={120} autoComplete="email" dir="ltr" /></label>
              <label>
                <span>{c.password}</span>
                <input name="password" type="password" required minLength={4} autoComplete="new-password" dir="ltr" />
                <small className="muted-small">{c.passwordHint}</small>
              </label>
              {TURNSTILE_ENABLED && (
                <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
              )}
              {error && <p className="co-error" role="alert">{error}</p>}
              <button className="lbtn center" type="submit" disabled={locked}>{locked ? c.busy : c.submit}</button>
              <p className="muted-small auth-terms">
                {c.terms[0]}<a href="/terms" target="_blank" rel="noopener noreferrer">{c.terms[1]}</a>
                {c.terms[2]}<a href="/privacy" target="_blank" rel="noopener noreferrer">{c.terms[3]}</a>{c.terms[4]}
              </p>
              <button type="button" className="linklike" onClick={() => setMode('login')}>{c.haveAccount}</button>
            </>
          ) : (
            <>
              <label><span>{c.email}</span><input name="email" required maxLength={120} autoComplete="username" dir="ltr" /></label>
              <label><span>{c.password}</span><input name="password" type="password" required autoComplete="current-password" dir="ltr" /></label>
              {error && <p className="co-error" role="alert">{error}</p>}
              <button className="lbtn center" type="submit" disabled={locked}>{locked ? c.busy : c.submit}</button>
              <div className="auth-links">
                <a className="linklike" href="/auth/forgot-password">{c.forgot}</a>
                <button type="button" className="linklike" onClick={() => setMode('register')}>{c.noAccount}</button>
              </div>
            </>
          )}
        </form>
      )}
    </dialog>
  );
}

// Customer video testimonials: thumbnails that open the YouTube video in a dialog.
function Reviews() {
  const c = t.reviews;
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(null);
  const ref = useDialog(Boolean(open));
  const list = all ? TESTIMONIALS : TESTIMONIALS.slice(0, 12);
  return (
    <section id="reviews" aria-labelledby="reviews-h">
      <div className="reviews-head">
        <div className="sec-head flush">
          <span className="eyebrow">{c.eyebrow}</span>
          <h2 id="reviews-h"><Highlighted parts={c.title} /></h2>
          <p className="lede">{c.lede}</p>
        </div>
        <span className="reviews-stars"><i aria-hidden="true">★★★★★</i>{c.stars}</span>
      </div>
      <ul className="reviews-grid">
        {list.map((v) => (
          <li key={v.id}>
            <button type="button" onClick={() => setOpen(v)} aria-label={c.play}>
              <img src={thumb(v.id)} alt="" loading="lazy" width="320" height="180" />
              <span className="yt-play" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      {!all && (
        <div className="reviews-more">
          <button type="button" className="lbtn ghost" onClick={() => setAll(true)}>{c.more}</button>
        </div>
      )}
      <dialog ref={ref} className="video-modal" onClose={() => setOpen(null)} onClick={(e) => e.target === ref.current && setOpen(null)}>
        {open && (
          <div className={open.short ? 'frame short' : 'frame'}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${open.id}?autoplay=1&rel=0`}
              title={c.play}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
        )}
        <button type="button" className="x" onClick={() => setOpen(null)} aria-label={c.close}>×</button>
      </dialog>
    </section>
  );
}

// Phones get the vertical 40-second reel; wider screens the full narrated walkthrough.
const isPhone = () => window.matchMedia('(max-width: 640px)').matches;

// The product tutorial (the same player as /tutorial-demo) in a full-screen dialog. It's
// portalled to <body>, outside .fbzl, so the landing styles can't reach the player.
// `variant` is null (closed), 'reel' or 'full' — picked when the button is pressed.
function TutorialDialog({ variant, onClose, onRegister }) {
  const open = Boolean(variant);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="lt-dialog" role="dialog" aria-modal="true" aria-label={t.tutorial.title}>
      <button type="button" className="lt-close" onClick={onClose} aria-label={t.auth.close}>×</button>
      {variant === 'reel' ? <ReelDemo embed /> : <TutorialDemo embed onRegister={onRegister} />}
    </div>,
    document.body,
  );
}

function TutorialSection({ onOpen }) {
  const c = t.tutorial;
  return (
    <section id="tutorial" aria-labelledby="tutorial-h">
      <div className="tutorial">
        <div className="sec-head flush">
          <span className="eyebrow">{c.eyebrow}</span>
          <h2 id="tutorial-h">{c.title}</h2>
          <p className="lede">{c.lede}</p>
          <ul className="tutorial-points">
            {c.points.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <button type="button" className="lbtn" onClick={onOpen}>
            <span aria-hidden="true">▶</span> {c.cta}
          </button>
        </div>
        <button
          type="button"
          className="tutorial-card"
          onClick={onOpen}
          aria-label={c.cta}
          style={{ backgroundImage: 'linear-gradient(135deg, rgb(15 22 41 / .82), rgb(24 119 242 / .55)), url(/media/misc/auth-bg.png)' }}
        >
          <span className="tutorial-play" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" /></svg>
          </span>
          <b>{c.cardTitle}</b>
          <small>{c.cardSub}</small>
        </button>
      </div>
    </section>
  );
}

// "Call me back" and contact forms. The app has no public inbox for these, so they open
// WhatsApp to support with the details filled in; the visitor just presses send.
function sendToWhatsApp(lines) {
  window.open(waLink(lines.filter(Boolean).join('\n')), '_blank', 'noopener');
}

function LeadForm() {
  const [sent, setSent] = useState(false);
  const c = t.lead;
  function submit(e) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    sendToWhatsApp(['היי, אשמח שתחזרו אליי לגבי FBZipper.', `שם: ${f.get('name')}`, `טלפון: ${f.get('phone')}`, f.get('email') && `אימייל: ${f.get('email')}`]);
    setSent(true);
  }
  return (
    <section className="lead-box" aria-labelledby="lead-h">
      <div className="lead-copy">
        <h2 id="lead-h">{c.title}</h2>
        <p className="lede">{c.lede}</p>
      </div>
      {sent ? (
        <p className="form-ok lead-done" role="status">{c.sent}</p>
      ) : (
        <form className="lead-form" onSubmit={submit}>
          <input name="name" required maxLength={80} autoComplete="name" placeholder={c.name} aria-label={c.name} />
          <input name="phone" type="tel" required minLength={7} maxLength={30} autoComplete="tel" dir="ltr" placeholder={c.phone} aria-label={c.phone} />
          <input name="email" type="email" maxLength={120} autoComplete="email" dir="ltr" placeholder={c.email} aria-label={c.email} />
          <button className="lbtn" type="submit">{c.submit}</button>
        </form>
      )}
    </section>
  );
}

function Contact() {
  const [sent, setSent] = useState(false);
  const c = t.contact;
  function submit(e) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    sendToWhatsApp([`שם: ${f.get('name')}`, `אימייל: ${f.get('email')}`, f.get('phone') && `טלפון: ${f.get('phone')}`, '', String(f.get('message') || '')]);
    setSent(true);
  }
  return (
    <section id="contact" aria-labelledby="contact-h">
      <div className="contact">
        <div className="sec-head flush">
          <span className="eyebrow">{c.eyebrow}</span>
          <h2 id="contact-h">{c.title}</h2>
          <p className="lede">{c.lede}</p>
        </div>
        <form className="contact-form" onSubmit={submit}>
          <div className="two">
            <label><span>{c.name}</span><input name="name" required maxLength={80} autoComplete="name" /></label>
            <label><span>{c.email}</span><input name="email" type="email" required maxLength={120} autoComplete="email" dir="ltr" /></label>
          </div>
          <label><span>{c.phone}</span><input name="phone" type="tel" maxLength={30} autoComplete="tel" dir="ltr" /></label>
          <label><span>{c.message}</span><textarea name="message" required maxLength={3000} rows={5} /></label>
          {sent && <p className="form-ok" role="status">{c.sent}</p>}
          <button className="lbtn" type="submit">{c.submit}</button>
        </form>
      </div>
    </section>
  );
}

// Floating WhatsApp support widget. "Online now" only during these Israel-time hours.
const SUPPORT_HOURS = { from: 8, to: 22 };
function supportOnline() {
  try {
    const h = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Asia/Jerusalem' }).format(new Date()));
    return h >= SUPPORT_HOURS.from && h < SUPPORT_HOURS.to;
  } catch {
    return true;
  }
}
function WhatsAppBubble() {
  const w = t.whatsapp;
  const [online, setOnline] = useState(supportOnline);
  useEffect(() => {
    const id = setInterval(() => setOnline(supportOnline()), 60000);
    return () => clearInterval(id);
  }, []);
  return (
    <a className="wa" href={waLink(w.message)} target="_blank" rel="noopener noreferrer" aria-label={`${w.title}: ${w.sub}. ${online ? w.online : w.offline}`}>
      <span className="wa-card">
        <strong>{w.title}</strong>
        <span className="wa-sub">{w.sub}</span>
        <span className={online ? 'wa-status on' : 'wa-status'}>
          <i aria-hidden="true" />
          {online ? w.online : w.offline}
        </span>
      </span>
      <span className="wa-bubble">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d={WA_PATH} /></svg>
        {online && <span className="wa-dot" aria-hidden="true" />}
      </span>
    </a>
  );
}

// Page setup: this route renders outside <App>, so hide the boot splash ourselves, and the
// design is light-only, so pin Metronic's theme to light while the page is open.
function usePageSetup() {
  useEffect(() => {
    const splash = document.getElementById('splash-screen');
    if (splash) splash.style.display = 'none';
    const root = document.documentElement;
    const prevTheme = root.getAttribute('data-bs-theme');
    const prevTitle = document.title;
    root.setAttribute('data-bs-theme', 'light');
    document.title = t.title;
    return () => {
      if (prevTheme) root.setAttribute('data-bs-theme', prevTheme);
      document.title = prevTitle;
    };
  }, []);
}

export default function LandingPage() {
  usePageSetup();
  const { currentUser } = useAuth();
  const aff = useReferral();
  const [authMode, setAuthMode] = useState(null); // null | 'register' | 'login'
  const [tutorial, setTutorial] = useState(null); // null | 'reel' | 'full'
  const openTutorial = () => setTutorial(isPhone() ? 'reel' : 'full');
  const closeTutorial = useCallback(() => setTutorial(null), []);
  const signedIn = Boolean(currentUser);
  // Every "start" button: open the free-trial signup, or go straight in when already signed in.
  const start = () => (signedIn ? window.location.assign(APP_HOME) : setAuthMode('register'));

  return (
    <div className="fbzl" dir="rtl" lang="he">
      <div className="wrap">
        <nav aria-label="Main">
          <a className="logo" href="#top"><Logo /></a>
          <div className="navlinks">
            <a href="#reviews">{t.nav.reviews}</a>
            <a href="#how">{t.nav.how}</a>
            <a href="#tutorial">{t.nav.tutorial}</a>
            <a href="#pricing">{t.nav.pricing}</a>
            <a href="#faq">{t.nav.faq}</a>
            <a href="#contact">{t.nav.contact}</a>
            {signedIn ? (
              <a href={APP_HOME} className="lbtn lsmall">{t.nav.app}</a>
            ) : (
              <>
                <button type="button" className="navlogin linklike" onClick={() => setAuthMode('login')}>{t.nav.login}</button>
                <button type="button" className="lbtn lsmall" onClick={start}>{t.nav.cta}</button>
              </>
            )}
          </div>
        </nav>

        <header className="hero" id="top">
          <div className="copy">
            <span className="eyebrow">{t.hero.eyebrow}</span>
            <h1><Highlighted parts={t.hero.title} /></h1>
            <p className="lede">{t.hero.lede}</p>
            <div className="ctas">
              <button type="button" className="lbtn" onClick={start}>
                {t.hero.cta} <span aria-hidden="true">{t.arrow}</span>
              </button>
              <a className="lbtn ghost" href="#top">{t.hero.demo}</a>
            </div>
            <span className="lbadge">
              <span aria-hidden="true">👨‍💻</span>
              {t.hero.badge}
            </span>
            <div className="proof">
              {t.hero.proof.map((p) => <span key={p}>{p}</span>)}
            </div>
          </div>
          <Demo t={t} onTrial={start} />
        </header>

        <Reviews />

        <section aria-labelledby="math-h">
          <div className="sec-head">
            <span className="eyebrow">{t.math.eyebrow}</span>
            <h2 id="math-h">{t.math.title}</h2>
          </div>
          <div className="math">
            <div className="by-hand">
              <span className="eyebrow">{t.math.handLabel}</span>
              <span className="big">{t.math.handBig}</span>
              <p>{t.math.hand}</p>
            </div>
            <div className="with">
              <span className="eyebrow">{t.math.withLabel}</span>
              <span className="big">{t.math.withBig}</span>
              <p>{t.math.with}</p>
            </div>
          </div>
        </section>

        <section aria-labelledby="value-h">
          <div className="value">
            <div className="sec-head flush">
              <span className="eyebrow">{t.value.eyebrow}</span>
              <h2 id="value-h"><Highlighted parts={t.value.title} /></h2>
              <p className="lede">{t.value.lede}</p>
              <div className="value-compare">
                {t.value.compare.map(([who, cost]) => (
                  <div className="value-row bad" key={who}><span>{who}</span><b>{cost}</b></div>
                ))}
                <div className="value-row good">
                  <span>{t.value.us}</span>
                  <b>{t.value.usPrice}<small>{t.value.usSub}</small></b>
                </div>
              </div>
            </div>
            <ul className="value-items">
              {t.value.items.map(([icon, title, body]) => (
                <li key={title}>
                  <i aria-hidden="true">{icon}</i>
                  <b>{title}</b>
                  <p>{body}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="how" aria-labelledby="how-h">
          <div className="sec-head">
            <span className="eyebrow">{t.how.eyebrow}</span>
            <h2 id="how-h">{t.how.title}</h2>
          </div>
          <ol className="steps">
            {t.how.steps.map(([title, body]) => (
              <li key={title}>
                <h3>{title}</h3>
                <p>{body}</p>
              </li>
            ))}
          </ol>
        </section>

        <TutorialSection onOpen={openTutorial} />

        <section aria-labelledby="uses-h">
          <div className="sec-head">
            <span className="eyebrow">{t.uses.eyebrow}</span>
            <h2 id="uses-h">{t.uses.title}</h2>
          </div>
          <div className="uses">
            {t.uses.items.map(([title, body, quote]) => (
              <div key={title}>
                <h3>{title}</h3>
                <p>{body}</p>
                <p className="quote-line">{quote}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="features" aria-labelledby="features-h">
          <div className="sec-head">
            <span className="eyebrow">{t.features.eyebrow}</span>
            <h2 id="features-h">{t.features.title}</h2>
          </div>
          <ul className="features">
            {t.features.items.map(([icon, title, body]) => (
              <li key={title}>
                <span className="f-icon" aria-hidden="true">{icon}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section id="pricing" aria-labelledby="pricing-h">
          <div className="pricing">
            <div className="sec-head flush">
              <span className="eyebrow">{t.pricing.eyebrow}</span>
              <h2 id="pricing-h">{t.pricing.title}</h2>
              <p className="lede">{t.pricing.lede}</p>
            </div>
            <div className="price-card">
              <div className="price">
                <span className="big" dir="ltr">{t.pricing.price}</span>
                <span>{t.pricing.per}</span>
              </div>
              <ul>
                {t.pricing.features.map((f) => <li key={f}>{f}</li>)}
              </ul>
              <button type="button" className="lbtn center" onClick={start}>{t.pricing.cta}</button>
              <p className="guarantee">{t.pricing.guarantee}</p>
            </div>
          </div>
        </section>

        <LeadForm />

        <section id="faq" aria-labelledby="faq-h">
          <div className="sec-head">
            <span className="eyebrow">{t.faq.eyebrow}</span>
            <h2 id="faq-h">{t.faq.title}</h2>
          </div>
          <div className="faq">
            {t.faq.items.map(([q, a]) => (
              <details key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>

        <Contact />

        <section className="final" aria-labelledby="final-h">
          <h2 id="final-h"><Highlighted parts={t.final.title} /></h2>
          <p className="final-sub">{t.final.sub}</p>
          <p className="final-together">{t.final.together}</p>
          <button type="button" className="lbtn" onClick={start}>
            {t.hero.cta} <span aria-hidden="true">{t.arrow}</span>
          </button>
        </section>

        <footer>
          <span>{t.footer.legal}</span>
          <span className="links">
            {t.footer.links.map((l, i) => (
              <a href={['/privacy', '/terms', '#contact'][i]} key={l}>{l}</a>
            ))}
          </span>
        </footer>
      </div>
      <AuthDialog mode={authMode} setMode={setAuthMode} aff={aff} />
      <TutorialDialog
        variant={tutorial}
        onClose={closeTutorial}
        onRegister={() => { setTutorial(null); start(); }}
      />
      <WhatsAppBubble />
    </div>
  );
}
